'use no memo';

import { createFileRoute } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect, useMemo } from 'react';
import { Card } from '@/components/Card/card';
import { DataTable, DataTablePagination, useTableDisplayState } from '@/components/DataTable';
import Icon, { IconSize } from '@/components/Icon/Icon';
import { HStack, VStack } from '@/components/Layout';
import SearchInput from '@/components/SearchInput';
import { Text } from '@/components/Typography';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { useFreeTextSearch, usePaginationSearch } from '@/lib/listPage';
import { ListFilterTabs, type ListFilterTabItem } from '../-components/ListFilterTabs';
import { isForbiddenError, ListForbiddenState } from '../-components/ListForbiddenState';
import { type IndividualLedgerScope, selectVisibleLedgerScopes, useLedgerDataScopes } from '../-query/useLedgerDataScopes';
import { useEquipmentColumns } from './-columns';
import { useEquipmentLedgerQuery } from './-query';
import { SearchSchema } from './-zod-schema';
export const Route = createFileRoute('/_individual/individual/equipments/')({
  validateSearch: zodValidator(SearchSchema),
  component: EquipmentsPage
});
function EquipmentsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.equipments'
  });
  const {
    t: tc
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.common'
  });
  const [pagination, setPagination] = usePaginationSearch({
    search,
    onSearchChange: next => navigate({
      search: () => next
    })
  });
  const {
    searchText,
    debouncedSearchText,
    handleSearchTextChange
  } = useFreeTextSearch({
    pagination,
    setPagination
  });

  // 使用可能なデータスコープはセッションに畳み込まれている（DC-8324）。
  const dataScopes = useLedgerDataScopes();
  const visibleScopes = useMemo(() => selectVisibleLedgerScopes(dataScopes, 'equipment'), [dataScopes]);
  const effectiveScope: IndividualLedgerScope = visibleScopes.includes(search.scope) ? search.scope : visibleScopes[0] ?? search.scope;
  useEffect(() => {
    if (visibleScopes.length > 0 && search.scope !== effectiveScope) {
      void navigate({
        search: prev => ({
          ...prev,
          scope: effectiveScope,
          pageIndex: 0
        }),
        replace: true
      });
    }
  }, [visibleScopes.length, search.scope, effectiveScope, navigate]);
  const {
    data,
    isPending,
    isError,
    error
  } = useEquipmentLedgerQuery({
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    scope: effectiveScope,
    freeText: debouncedSearchText === '' ? undefined : debouncedSearchText
  }, {
    enabled: visibleScopes.length > 0
  });
  const columns = useEquipmentColumns(effectiveScope !== 'self');
  const rows = data?.items ?? [];

  // oxlint-disable-next-line react-hooks-js/incompatible-library -- Compiler非互換のサードパーティAPI
  const table = useReactTable({
    data: rows,
    columns,
    pageCount: data?.maxPage ?? -1,
    state: {
      pagination
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true
  });
  const displayState = useTableDisplayState(isPending, rows);
  const scopeLabels: Record<IndividualLedgerScope, string> = {
    self: t('tabs.self'),
    'managed-place': t('tabs.managedPlace')
  };
  const tabs: ListFilterTabItem<IndividualLedgerScope>[] = visibleScopes.map(scope => ({
    value: scope,
    label: scopeLabels[scope]
  }));
  // granted な scope が 1 つでもあればタブを表示する（単一 scope でも表示）。
  const showScopeTabs = tabs.length > 0;
  if (visibleScopes.length === 0 || isError && isForbiddenError(error)) {
    return <div className="flex h-full min-w-0 flex-col overflow-hidden">
        <PageHeader title={t('pageTitle')} />
        <VStack className="min-h-0 flex-1 gap-4 px-5 py-6 md:px-12 md:py-8">
          <ListForbiddenState onBack={() => void navigate({
          to: '/individual'
        })} />
        </VStack>
      </div>;
  }
  if (isError) {
    throw error;
  }
  return <div className="flex h-full min-w-0 flex-col overflow-hidden">
      <PageHeader title={t('pageTitle')} />
      <VStack className="min-h-0 flex-1 gap-4 px-5 py-6 md:px-12 md:py-8">
        <HStack className="items-center gap-2">
          {showScopeTabs && <ListFilterTabs value={effectiveScope} tabs={tabs} onChange={scope => navigate({
          search: prev => ({
            ...prev,
            scope,
            pageIndex: 0
          })
        })} />}
          <div className="w-full max-w-xs">
            <SearchInput value={searchText} onChange={e => handleSearchTextChange(e.target.value)} placeholder={tc('search')} />
          </div>
        </HStack>
        <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <DataTable table={table} displayState={displayState} onRowClick={null} />
        </Card>
        <DataTablePagination table={table} totalCount={data?.count} pageOptions={[20, 50, 100]} />
      </VStack>
    </div>;
}
function PageHeader({
  title
}: {
  title: string;
}) {
  return <div className="flex shrink-0 items-center gap-3 border-b border-neutral-muted-border bg-neutral-fill px-5 py-3 shadow-sm md:px-12 md:py-4">
      <span className="text-neutral-on-fill">
        <Icon iconName="Package" size={IconSize.XL} inheritColor />
      </span>
      <Text as="h1" variant="h3">
        {title}
      </Text>
    </div>;
}